import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow, Skeleton } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  SportsFootball as FootballIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as SoccerIcon,
  SportsHockey as HockeyIcon,
  SportsScore as DefaultSportIcon,
  LiveTv as LiveIcon,
  Timer as TimerIcon,
  Star as PremiumIcon,
  Settings as SettingsIcon,
  Public as PublicIcon,
  Sync as SyncIcon,
  Palette as ThemeIcon,
  Share as ShareIcon,
  EmojiEvents as WinnerIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';
import { sportsApi } from '../../api';
import type { SportConfig } from '../../types';

const sportIconMap: Record<string, React.ReactNode> = {
  nfl: <FootballIcon fontSize="small" sx={{ color: '#8B4513' }} />,
  ncaaf: <FootballIcon fontSize="small" sx={{ color: '#8B4513' }} />,
  nba: <BasketballIcon fontSize="small" sx={{ color: '#ff6600' }} />,
  ncaab: <BasketballIcon fontSize="small" sx={{ color: '#ff6600' }} />,
  wnba: <BasketballIcon fontSize="small" sx={{ color: '#ff6600' }} />,
  nhl: <HockeyIcon fontSize="small" sx={{ color: '#1976d2' }} />,
  mls: <SoccerIcon fontSize="small" sx={{ color: '#4caf50' }} />,
};

function getPeriodLabel(quarters: number): string {
  if (quarters === 2) return '2 halves';
  if (quarters === 3) return `3 periods`;
  return `${quarters} quarters`;
}

function getPeriodNames(quarters: number): string {
  if (quarters === 2) return '1st Half, 2nd Half';
  if (quarters === 3) return Array.from({ length: 3 }, (_, i) => `P${i + 1}`).join(', ');
  return Array.from({ length: quarters }, (_, i) => `Q${i + 1}`).join(', ');
}

export const LiveGamesSupportPage: React.FC = () => {
  useDocumentTitle('Live Games - Support');
  const [sports, setSports] = useState<SportConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sportsApi.getEnabledSports()
      .then(setSports)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <SupportLayout
      title="Live Games"
      description="Connect your squares boards to real sports games for live scores, countdowns, team logos, and automatic game tracking."
      breadcrumbs={[{ label: 'Live Games' }]}
    >
      {/* Overview */}
      <Box id="overview" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <LiveIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          What Are Live Games?
        </Typography>

        <Typography variant="body1" paragraph>
          Live Games let you connect a SquareUp board to a real sports game. When you link a board to a live game, you get <strong>automatic team names</strong>, <strong>team logos</strong>, <strong>live countdown timers</strong>, and <strong>real-time score updates</strong> — all without any manual work.
        </Typography>

        <Typography variant="body1" paragraph>
          Instead of typing in team names and manually tracking scores, the board pulls everything directly from the game data. Your board header shows team logos, team colors, and a live banner that updates throughout the game.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%', textAlign: 'center' }}>
              <TimerIcon sx={{ fontSize: 40, color: '#509ed3', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Countdown Timer</Typography>
              <Typography variant="body2" color="text.secondary">
                Animated countdown to game start with live digit tiles
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%', textAlign: 'center' }}>
              <LiveIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Live Scores</Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time score display with quarter and time remaining
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%', textAlign: 'center' }}>
              <ThemeIcon sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Team Branding</Typography>
              <Typography variant="body2" color="text.secondary">
                Team logos and colors automatically applied to the board
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%', textAlign: 'center' }}>
              <ShareIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Public Pages</Typography>
              <Typography variant="body2" color="text.secondary">
                Live banners and logos visible on shared public board links
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Supported Sports */}
      <Box id="supported-sports" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <FootballIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Available Sports
        </Typography>

        <Typography variant="body1" paragraph>
          The following sports are currently enabled for live game integration. Sports can be enabled or disabled by the site administrator, so this list reflects what's available right now.
        </Typography>

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          </Box>
        ) : sports.length === 0 ? (
          <Alert severity="info">
            No sports are currently enabled. Check back later or contact the site administrator.
          </Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Sport</strong></TableCell>
                <TableCell><strong>Periods</strong></TableCell>
                <TableCell><strong>Period Labels</strong></TableCell>
                <TableCell><strong>Season</strong></TableCell>
                <TableCell><strong>Live Board Cost</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sports.map((sport) => (
                <TableRow key={sport.sportKey}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sportIconMap[sport.sportKey] || <DefaultSportIcon fontSize="small" />}
                      {sport.displayName}
                    </Box>
                  </TableCell>
                  <TableCell>{getPeriodLabel(sport.quarters)}</TableCell>
                  <TableCell>{getPeriodNames(sport.quarters)}</TableCell>
                  <TableCell>{sport.seasonYear}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${sport.liveBoardCreditCost} credit${sport.liveBoardCreditCost !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Season Availability" variant="info" icon={<SyncIcon />}>
            <Typography variant="body2">
              Games are available based on the current season configured by your admin. Only upcoming and in-progress games are shown when creating a board. Past games are not available for selection.
            </Typography>
          </InfoCard>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Creating a Live Board */}
      <Box id="creating-live-board" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PremiumIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Creating a Live Game Board
        </Typography>

        <Typography variant="body1" paragraph>
          Live boards are a <strong>premium feature</strong> that costs additional credits. When you select a live game during board creation, the credit cost is clearly displayed before you confirm.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Step-by-Step
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon><Chip label="1" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Go to Create Board"
              secondary="Navigate to the board creation page from your dashboard."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="2" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Select a Sport"
              secondary="Choose from the available sports. Only sports that have been enabled will appear."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="3" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Pick a Game"
              secondary="Browse upcoming games for the selected sport. Each game shows the teams, date, time, and venue. Click the game to select it."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="4" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Review the Premium Indicator"
              secondary='Once a game is selected, a "Premium Live Board" indicator appears showing the credit cost. This confirms the board will be linked to live game data.'
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="5" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Configure Board Settings"
              secondary="Set your board name, participation mode, and other settings. Note that team names are automatically set from the game and cannot be changed."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><Chip label="6" size="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Create the Board"
              secondary="Confirm creation. The credit cost (including the live board premium) will be deducted from your balance."
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          You can also create boards without selecting a game. Non-live boards work exactly as before — you manually enter team names and record scores yourself.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* What Gets Locked */}
      <Box id="locked-settings" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Settings on Live Boards
        </Typography>

        <Typography variant="body1" paragraph>
          When a board is linked to a live game, certain settings are managed automatically and cannot be changed manually.
        </Typography>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Setting</strong></TableCell>
              <TableCell><strong>Behavior</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Team A / Team B names</TableCell>
              <TableCell>
                <Chip label="Read-only" size="small" color="warning" sx={{ mr: 1 }} />
                Set from the live game data. Cannot be edited.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Game date</TableCell>
              <TableCell>
                <Chip label="Read-only" size="small" color="warning" sx={{ mr: 1 }} />
                Set from the scheduled game date and time.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Team logos</TableCell>
              <TableCell>
                <Chip label="Automatic" size="small" color="success" sx={{ mr: 1 }} />
                Pulled from team mappings. Shown on the grid, public page, and exports.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Team colors</TableCell>
              <TableCell>
                <Chip label="Automatic" size="small" color="success" sx={{ mr: 1 }} />
                Applied to grid headers when using classic or no theme.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Board name</TableCell>
              <TableCell>
                <Chip label="Editable" size="small" color="primary" sx={{ mr: 1 }} />
                You can still customize the board name.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Payout rules</TableCell>
              <TableCell>
                <Chip label="Editable" size="small" color="primary" sx={{ mr: 1 }} />
                All payout settings remain fully configurable.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Theme</TableCell>
              <TableCell>
                <Chip label="Editable" size="small" color="primary" sx={{ mr: 1 }} />
                You can apply any theme. Live team colors are used as fallback on classic/no theme.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Live Game Banner */}
      <Box id="live-banner" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <LiveIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          The Live Game Banner
        </Typography>

        <Typography variant="body1" paragraph>
          Every live board displays a <strong>Live Game Banner</strong> above the grid. This banner adapts based on the game's current state.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Banner States
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: '#509ed3', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                <TimerIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                Countdown Mode
              </Typography>
              <Typography variant="body2" paragraph>
                Before the game starts, the banner shows an animated countdown timer with days, hours, minutes, and seconds. Team logos and abbreviations flank the countdown.
              </Typography>
              <List dense>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Animated digit tiles with smooth transitions" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Urgent styling when under 1 hour" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Shimmer effect when under 5 minutes" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Game date and venue displayed below" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: '#ef4444', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="error.main">
                <LiveIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                Live Mode
              </Typography>
              <Typography variant="body2" paragraph>
                Once the game is in progress, the banner switches to a live scoreboard showing the current score, quarter/period, and time remaining.
              </Typography>
              <List dense>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary='Pulsing "LIVE" indicator' primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Score updates every 30 seconds" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Quarter/period and time remaining" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Leading team highlighted" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: '#6b7280', height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#6b7280' }}>
                <WinnerIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                Final
              </Typography>
              <Typography variant="body2">
                After the game ends, the banner shows the final score with both team logos and abbreviations. The status changes to "FINAL."
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: '#f59e0b', height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#f59e0b' }}>
                Postponed / Canceled
              </Typography>
              <Typography variant="body2">
                If a game is postponed or canceled, the banner updates to show the appropriate status. Your board and all claimed squares remain intact.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Team Logos and Colors */}
      <Box id="team-branding" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ThemeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Team Logos and Colors
        </Typography>

        <Typography variant="body1" paragraph>
          Live boards automatically display team logos and apply team colors throughout the board.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Where Team Logos Appear
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Grid Headers"
              secondary="Logos appear next to both team names on the top and left axis of the grid."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Live Game Banner"
              secondary="Logos are displayed in the countdown timer and live score sections."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Public Board Page"
              secondary="Anyone viewing the shared board link sees logos and the live banner."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Board Export / Download"
              secondary="When you download the board as an image, team logos are included in the export."
            />
          </ListItem>
        </List>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Team Colors on Classic Theme" variant="tip" icon={<ThemeIcon />}>
            <Typography variant="body2">
              When using the <strong>Classic</strong> theme or no theme at all, the board automatically applies each team's official primary color to the grid headers. If you use a custom theme, the theme's colors take priority instead.
            </Typography>
          </InfoCard>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Public Board Page */}
      <Box id="public-sharing" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PublicIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Public Board Sharing
        </Typography>

        <Typography variant="body1" paragraph>
          Live boards work fully on <strong>public board pages</strong> — no login required for viewers. When you share your board's public link, visitors see everything:
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Live game banner with countdown or live scores" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Team logos on grid headers" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Team colors on classic/no theme boards" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="All claimed squares and player names" />
          </ListItem>
        </List>

        <Alert severity="success" sx={{ mt: 2 }}>
          <strong>No account needed.</strong> Anyone with the public link can view the board, the live game banner, and all game data. Only claiming squares requires being signed in.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Credits and Pricing */}
      <Box id="credits" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PremiumIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Credits and Pricing
        </Typography>

        <Typography variant="body1" paragraph>
          Live boards cost additional credits on top of the standard board creation cost. The exact premium is set by your site administrator and may vary by sport.
        </Typography>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Cost Type</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Standard board cost</TableCell>
              <TableCell>The base credit cost for creating any board (based on your tier).</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Live board premium</TableCell>
              <TableCell>Additional credits charged when linking to a live game. Configured per sport by the admin.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Total cost</TableCell>
              <TableCell>Standard cost + live board premium = total credits deducted.</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Credit Cost Visibility" variant="info" icon={<PremiumIcon />}>
            <Typography variant="body2">
              The total credit cost is always shown before you create the board. The premium indicator on the creation page clearly displays how many credits will be charged so there are no surprises.
            </Typography>
          </InfoCard>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      <Box id="faq" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Frequently Asked Questions
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Can I change the teams after creating a live board?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No. Team names on live boards are set by the game data and cannot be edited. This ensures the board always reflects the correct matchup.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            What happens if a game is postponed or canceled?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your board and all claimed squares remain intact. The live banner updates to show the postponed or canceled status. You can still manage the board normally.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Do I still need to record scores manually?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The live banner shows scores automatically, but recording quarter hits on the board is still done by the board manager. The live score display helps you know when to record each quarter's result.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Can I create a board without linking to a live game?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Yes. Selecting a live game is completely optional. You can create boards the traditional way by typing in team names and managing scores manually.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Why don't I see any games to select?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Games are only available when a sport is enabled by the site admin and games have been synced for the current season. If no games appear, the sport may be in its off-season or hasn't been configured yet.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Do team logos appear in the downloaded board image?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Yes. When you export or download your board as an image, team logos are included in both the top and left team headers.
          </Typography>
        </Box>
      </Box>
    </SupportLayout>
  );
};

export default LiveGamesSupportPage;
